/**
 * The Pawsitive Deck: every edition fact the /products page shows, in one place.
 *
 * Every edition is a proposal. Nothing here is for sale, so nothing here may read as stock,
 * a price someone can pay today, or a guaranteed donation. Money is integer cents; the
 * estimated voucher contribution is derived (price minus estimated manufacturing cost),
 * never stored, so the two figures cannot drift apart.
 *
 * Images: `null` renders a labelled placeholder. A supplied image lives in `images/` and is
 * imported (webpack emits it under a content hash, so a replaced picture is never held by a
 * browser's year-long cache), at full size and at half size (`-sm`) for small screens. The
 * shots are composed from the deck art (claudedocs/pawsitive-deck/web/ on the deck branch of the
 * monorepo); none is final production artwork, so each is labelled a concept render.
 */
import everydayHero from './images/everyday-hero.webp';
import everydayHeroSm from './images/everyday-hero-sm.webp';
import everydayDetail from './images/everyday-detail.webp';
import everydayDetailSm from './images/everyday-detail-sm.webp';
import foundersHero from './images/founders-hero.webp';
import foundersHeroSm from './images/founders-hero-sm.webp';
import foundersDetail from './images/founders-detail.webp';
import foundersDetailSm from './images/founders-detail-sm.webp';
import pieceDeck from './images/piece-deck.webp';
import pieceDeckSm from './images/piece-deck-sm.webp';
import pieceTuckBox from './images/piece-tuck-box.webp';
import pieceTuckBoxSm from './images/piece-tuck-box-sm.webp';
import pieceJokers from './images/piece-jokers.webp';
import pieceJokersSm from './images/piece-jokers-sm.webp';
import pieceGoldenPaw from './images/piece-golden-paw.webp';
import pieceGoldenPawSm from './images/piece-golden-paw-sm.webp';
import piecePresentationBox from './images/piece-presentation-box.webp';
import piecePresentationBoxSm from './images/piece-presentation-box-sm.webp';
import pieceCertificate from './images/piece-certificate.webp';
import pieceCertificateSm from './images/piece-certificate-sm.webp';
import piecePouch from './images/piece-pouch.webp';
import piecePouchSm from './images/piece-pouch-sm.webp';

export type EditionId = 'budget' | 'everyday' | 'premium' | 'founders';

export interface ProductImage {
  readonly src: string;
  readonly srcSet?: string;
  readonly alt: string;
  /** Labels the picture a concept render rather than the finished product. */
  readonly concept?: boolean;
}

/** A composed concept shot, full width `width` px, with its half-size copy for small screens. */
const shot = (src: string, small: string, width: number, alt: string): ProductImage => ({
  src,
  srcSet: `${small} ${width / 2}w, ${src} ${width}w`,
  alt,
  concept: true,
});

/** Tailwind classes per edition. Fixed hex values are bands that stay dark in both themes. */
export interface EditionTheme {
  readonly surface: string;
  readonly ink: string;
  readonly muted: string;
  readonly accent: string;
  readonly button: string;
  readonly border: string;
  /** True when the surface is dark in both themes, so the placeholder uses the gold/white mark. */
  readonly dark: boolean;
}

export interface Edition {
  readonly id: EditionId;
  readonly name: string;
  readonly shortName: string;
  readonly priceCents: number;
  readonly estimatedManufacturingCostCents: number;
  /** Proposed production cap; null where none is planned. */
  readonly productionLimit: number | null;
  /** Listed by name and price only, as "Coming soon": out of the hero, listings, table, chart and vote. */
  readonly comingSoon?: boolean;
  readonly hero: {
    readonly headline: string;
    readonly subheading: string;
    readonly cta: string;
    readonly image: ProductImage | null;
  };
  readonly description: string;
  /** Three to five lines for the summary card. */
  readonly highlights: readonly string[];
  readonly features: readonly string[];
  readonly bestFor: string;
  readonly note?: string;
  readonly detailImage: ProductImage | null;
  readonly theme: EditionTheme;
}

export const FOUNDERS_GOLD = '#d4af37';

export const FOUNDERS_THEME: EditionTheme = {
  surface: 'bg-black',
  ink: 'text-white',
  muted: 'text-[#d6d3cb]',
  accent: 'text-[#d4af37]',
  button: 'bg-[#d4af37] text-black hover:bg-[#e2c15a] rounded-full font-medium transition-colors duration-200',
  border: 'border-[#d4af37]',
  dark: true,
};

export const EDITIONS: readonly Edition[] = [
  {
    id: 'budget',
    name: 'Budget Edition',
    shortName: 'Budget',
    priceCents: 1000,
    estimatedManufacturingCostCents: 250,
    productionLimit: null,
    comingSoon: true,
    hero: {
      headline: 'A Little Play. A Lot of Purpose.',
      subheading: 'Our most accessible deck. Every game helps support veterinary care for pets in need.',
      cta: 'Explore Budget Edition',
      image: null,
    },
    description:
      'The original Pawsitive Deck experience at an accessible price. A complete custom deck designed to turn every game into support for pets.',
    highlights: [
      'Four custom animal-themed suits',
      'Custom illustrated Kings, Queens, Jacks, and Aces',
      'Standard coated cardstock',
      'Traditional playable deck with two Jokers',
    ],
    features: [
      'Standard coated cardstock.',
      'Four custom animal-themed suits: Hearts, Dogs, Cats, and Paws.',
      'Custom illustrated Kings, Queens, Jacks, and Aces.',
      'Standard printed tuck box.',
      'Traditional playable deck with two Jokers.',
      'No specialty finishes or exclusive collectibles.',
    ],
    bestFor: 'Casual players, gifts, and supporters looking for an accessible way to contribute.',
    detailImage: null,
    theme: {
      surface: 'bg-blue-50',
      ink: 'text-gray-900',
      muted: 'text-gray-700',
      accent: 'text-primary',
      button: 'btn-primary',
      border: 'border-primary',
      dark: false,
    },
  },
  {
    id: 'everyday',
    name: 'Everyday Edition',
    shortName: 'Everyday',
    priceCents: 2000,
    estimatedManufacturingCostCents: 450,
    productionLimit: null,
    hero: {
      headline: 'Made to Play. Made to Help.',
      subheading: 'Thick, durable playing cards featuring our original custom animal-themed artwork.',
      cta: 'Explore Everyday Edition',
      image: shot(everydayHero, everydayHeroSm, 1600, 'Concept render of the Everyday Edition: its tuck box and a fan of Pawsitive Deck cards'),
    },
    description:
      'A thicker, more durable deck built for regular play, based on the quality and feel of our physical prototype.',
    highlights: [
      'Approximately 310gsm black-core cardstock',
      'Smooth protective coating',
      'Complete custom court-card artwork',
      'Upgraded printed tuck box',
    ],
    features: [
      'Approximately 310gsm black-core cardstock.',
      'Smooth protective coating.',
      'Increased stiffness and durability.',
      'Four custom animal-themed suits.',
      'Complete custom court-card artwork.',
      'Upgraded printed tuck box.',
      'Standard Jokers.',
    ],
    bestFor: 'Families, game nights, and frequent players.',
    note: 'This edition is closest to our current physical prototype.',
    detailImage: shot(everydayDetail, everydayDetailSm, 1200, 'Everyday Edition cards, card back and tuck box'),
    theme: {
      surface: 'bg-green-50',
      ink: 'text-gray-900',
      muted: 'text-gray-700',
      accent: 'text-secondary',
      button: 'btn-secondary',
      border: 'border-secondary',
      dark: false,
    },
  },
  {
    id: 'premium',
    name: 'Premium Edition',
    shortName: 'Premium',
    priceCents: 5000,
    estimatedManufacturingCostCents: 750,
    productionLimit: null,
    comingSoon: true,
    hero: {
      headline: 'Elevate the Game. Expand the Impact.',
      subheading: 'Premium card finishes, metallic detailing, and exclusive illustrated Jokers.',
      cta: 'Explore Premium Edition',
      image: null,
    },
    description:
      'A refined edition combining upgraded playing-card materials, collectible artwork, and premium packaging.',
    highlights: [
      'Premium linen-finish cardstock',
      'Gold-foil-stamped tuck box',
      'Metallic detailing throughout the artwork',
      'Two exclusive illustrated Jokers',
    ],
    features: [
      'Premium linen-finish cardstock.',
      'Gold-foil-stamped tuck box.',
      'Metallic detailing throughout the card artwork.',
      'Two exclusive illustrated Jokers.',
      'Enhanced printed packaging and presentation.',
      'All standard playable cards.',
    ],
    bestFor: 'Card enthusiasts, collectors, and premium gifting.',
    note: "A premium playing deck. It does not include the Founder's Edition collector's kit.",
    detailImage: null,
    theme: {
      surface: 'bg-[#13233f]',
      ink: 'text-white',
      muted: 'text-[#c9d6e8]',
      accent: 'text-[#9cc4ec]',
      button: 'bg-white text-[#13233f] hover:bg-[#e6eef8] rounded-full font-medium transition-colors duration-200',
      border: 'border-[#13233f]',
      dark: true,
    },
  },
  {
    id: 'founders',
    name: "Founder's Edition",
    shortName: "Founder's",
    priceCents: 10000,
    estimatedManufacturingCostCents: 2000,
    productionLimit: 500,
    hero: {
      headline: "A Collector's Deck. A Lasting Impact.",
      subheading:
        "An exclusive black, white, and gold collector's set with a numbered, founder-signed collectible card.",
      cta: "Explore Founder's Edition",
      image: shot(foundersHero, foundersHeroSm, 1600, "Concept render of the Founder's Edition: the black and gold tuck box, a fan of cards and the Golden Paw card"),
    },
    description:
      "Our most exclusive collection. A limited-edition black, white, and gold playing-card experience featuring luxury packaging, collectible artwork, and a special card signed by the foundation's founders.",
    highlights: [
      'Exclusive black, white, and gold deck',
      'Gold-gilded card edges',
      'Individually numbered, founder-signed Golden Paw card',
      "Luxury collector's presentation box",
      'Certificate of Purpose and branded pouch',
    ],
    features: [
      'Exclusive monochromatic black, white, and gold deck.',
      'Gold-gilded card edges.',
      'Premium protective finish.',
      'Ornate gold-foil card backs.',
      'Special Staffy/pit bull King illustration.',
      'Two exclusive premium Jokers.',
      'Individually numbered Golden Paw collectible card.',
      'Founder signatures.',
      "Luxury collector's presentation box.",
      'Certificate of Purpose.',
      'Branded protective pouch.',
    ],
    bestFor: 'Collectors, founding supporters, and donors who want a permanent keepsake of their contribution.',
    detailImage: shot(foundersDetail, foundersDetailSm, 1200, "Founder's Edition card back, Golden Paw card and tuck box"),
    theme: FOUNDERS_THEME,
  },
];

/** The editions the page presents in full; the rest appear only as "Coming soon". */
export const OFFERED = EDITIONS.filter((e) => !e.comingSoon);

export const estimatedContributionCents = (edition: Edition): number =>
  edition.priceCents - edition.estimatedManufacturingCostCents;

/** "$10" for whole dollars, "$7.50" otherwise. */
export const formatPrice = (cents: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);

export interface FoundersPiece {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly details: readonly string[];
  /** Customer-facing caveat: what is not final yet. */
  readonly caveat?: string;
  readonly image: ProductImage | null;
}

/**
 * The seven pieces of the Founder's collection. Two brand rules hold here: the tuck box FRONT
 * keeps the crowned dog portrait (the official logo replaces the emblem on the BACK only), and
 * the Golden Paw card shows no signature until a founder has signed the physical card.
 */
export const FOUNDERS_PIECES: readonly FoundersPiece[] = [
  {
    id: 'deck',
    title: 'Exclusive Black-and-Gold Playing Deck',
    summary:
      'A complete playable deck that keeps the Pawsitive animal-themed concept in an exclusive luxury visual identity, designed as one cohesive collector’s edition.',
    details: [
      'Black, white, and gold color palette.',
      'Premium playing-card stock with a smooth protective surface.',
      'Gold-gilded edges and ornate gold-foil detailing.',
      'Custom animal-themed suits.',
      'A crowned Staffordshire-type pit bull on the King card.',
      'Detailed coordinating illustrations throughout the face cards.',
      'Custom backs featuring the Pawsitive Future Foundation logo in monochromatic gold.',
    ],
    image: shot(pieceDeck, pieceDeckSm, 1200, 'Black, white and gold playing cards fanned out'),
  },
  {
    id: 'tuck-box',
    title: "Exclusive Collector's Tuck Box",
    summary: 'A premium tuck box for the deck, separate from the larger presentation case.',
    details: [
      'Matte-black printed exterior with gold-foil ornamental borders.',
      'Front: a crowned dog portrait.',
      'Back: an ornate gold design with the official Pawsitive logo.',
      'The foundation name and “Stack the Deck in Their Favor” messaging.',
      'Coordinating gold typography on the spine.',
    ],
    image: shot(pieceTuckBox, pieceTuckBoxSm, 1200, "The Founder's tuck box: the crowned dog on the front, the logo on the back"),
  },
  {
    id: 'jokers',
    title: 'Two Exclusive Premium Jokers',
    summary: 'A matched pair of collectible Joker cards, distinct from the standard edition Jokers.',
    details: [
      'Exclusive black, white, and gold illustrations.',
      'Royal court-jester styling with ornate costume detailing.',
      'One primarily white card and one primarily black card.',
      'Matching gold accents and complementary compositions.',
    ],
    caveat: 'The animal characters for the Joker pair are still being finalized; artwork is not yet approved.',
    image: shot(pieceJokers, pieceJokersSm, 1200, 'The white and the black Joker'),
  },
  {
    id: 'golden-paw',
    title: 'Founder-Signed Golden Paw Card',
    summary:
      'The principal collectible: a 55th card, separate from the playable deck, made as a keepsake of your support.',
    details: [
      'Metallic-gold appearance with decorative radial and ornamental detailing.',
      'The official Pawsitive Future Foundation logo in monochromatic gold.',
      'Individually numbered, for example “042 / 500”.',
      'The message: “Thank you for helping create a brighter future for pets.”',
      'A signature area for the two founders, Eric Hodonsky and Michelle Honeyman.',
    ],
    caveat: 'Each physical card is intended to be signed by hand by both founders; the signatures and names in these renders are illustrations.',
    image: shot(pieceGoldenPaw, pieceGoldenPawSm, 1200, 'The numbered Golden Paw card, with placeholder names under its signatures'),
  },
  {
    id: 'presentation-box',
    title: "Collector's Presentation Box",
    summary:
      'A separate rigid presentation case that houses the complete collector’s set. It is a different piece from the deck’s tuck box.',
    details: [
      'Matte-black exterior with gold decorative trim.',
      'The official Pawsitive logo in monochromatic gold.',
      'Fitted interior compartments for the deck and the collector’s extras.',
      'Interior message: “Same Games. Brighter Tomorrows.”',
      'A premium, gift-ready presentation.',
    ],
    image: shot(piecePresentationBox, piecePresentationBoxSm, 1200, "The collector's presentation box, open, holding the deck and the Golden Paw card"),
  },
  {
    id: 'certificate',
    title: 'Certificate of Purpose',
    summary: 'A commemorative certificate recording that your purchase supports the voucher program.',
    details: [
      'Black cardstock with gold typography.',
      'The official Pawsitive logo, foundation name, and branding.',
      'An edition serial number matching your set.',
      'Heading: “Certificate of Purpose.”',
      'Body: “This deck helped fund spay and neuter care for pets in need.”',
    ],
    caveat: 'A deck contributes to the voucher pool; it does not by itself fund an entire procedure.',
    image: shot(pieceCertificate, pieceCertificateSm, 1200, 'The Certificate of Purpose'),
  },
  {
    id: 'pouch',
    title: 'Branded Protective Pouch',
    summary: 'A soft protective pouch for the deck, included with the collection rather than sold separately.',
    details: [
      'Black velvet-style material with a drawstring closure.',
      'The Pawsitive logo in monochromatic gold.',
      'Sized to hold the deck’s tuck box.',
      'Coordinated with the rest of the collection.',
    ],
    image: shot(piecePouch, piecePouchSm, 1200, 'The black velvet pouch with the gold logo'),
  },
];

export const COMPARISON: readonly { readonly feature: string; readonly editions: readonly EditionId[] }[] = [
  { feature: 'Complete custom playing deck', editions: ['budget', 'everyday', 'premium', 'founders'] },
  { feature: 'Custom animal-themed suits', editions: ['budget', 'everyday', 'premium', 'founders'] },
  { feature: 'Thick black-core cardstock', editions: ['everyday', 'premium', 'founders'] },
  { feature: 'Premium playing finish', editions: ['premium', 'founders'] },
  { feature: 'Gold-foil packaging', editions: ['premium', 'founders'] },
  { feature: 'Exclusive Jokers', editions: ['premium', 'founders'] },
  { feature: 'Black/white/gold artwork', editions: ['founders'] },
  { feature: 'Gold-gilded edges', editions: ['founders'] },
  { feature: 'Numbered founder-signed card', editions: ['founders'] },
  { feature: "Collector's presentation box", editions: ['founders'] },
  { feature: 'Certificate of Purpose', editions: ['founders'] },
  { feature: 'Branded protective pouch', editions: ['founders'] },
];

/**
 * Working benchmark for a low-cost-clinic spay/neuter procedure. Not yet backed by a cited
 * source: confirm against a current one, and link it, before this page goes to production.
 */
export const PROCEDURE_COST_RANGE = { lowCents: 9000, highCents: 33000 };

export type InterestChoice = EditionId | 'all';

export const INTEREST_OPTIONS: readonly { readonly value: InterestChoice; readonly label: string }[] = [
  ...OFFERED.map((e) => ({ value: e.id, label: `${formatPrice(e.priceCents)} — ${e.name}` })),
  { value: 'all', label: OFFERED.length === 2 ? 'BOTH' : `ALL ${OFFERED.length}` },
];

/**
 * The proposed decision rule, for whoever reads the results (HubSpot; see README): if more than
 * this share of qualified interested supporters choose every edition offered, explore producing
 * the full range. Deliberately not shown on the page, and no tally is either.
 */
export const ALL_THRESHOLD = 0.5;
